import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useAuthStore } from "@/stores/auth.store";
import { authFetch } from "@/lib/auth-client";
import { CompactBalance } from "@/components/ui/BalanceDisplay";
import { NavContextStrip } from "@/components/layout/NavContextStrip";
import { Button } from "@/components/ui/button";
import { asset } from "@/lib/asset";

export function Navigation() {
  const {
    user,
    isAuthenticated,
    isLoading,
    isHydrated,
    accessToken,
    setUser,
    setLoading,
    logout,
  } = useAuthStore();

  useEffect(() => {
    const fetchUser = async () => {
      if (!isHydrated) return;

      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await authFetch("/api/auth/me");
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else if (res.status === 401) {
          logout();
        }
        // Don't logout on network errors or 500s — transient failures
      } catch {
        // Network error — don't logout
      }
    };
    fetchUser();
  }, [isHydrated, accessToken, setUser, setLoading, logout]);

  return (
    <nav className="sticky top-0 z-30 bg-surface/95 backdrop-blur-md border-b border-surface-3">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 min-h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <Image
              src={asset("logo.webp")}
              alt="FantasyPRIME"
              width={40}
              height={40}
              unoptimized
              className="group-hover:scale-105 transition-transform duration-200 object-contain"
            />
            <div className="flex items-baseline gap-1.5">
              <span className="font-serif font-light text-cream tracking-tight text-lg">
                Fantasy
              </span>
              <span className="font-serif font-bold text-gold tracking-tight text-lg">
                PRIME
              </span>
            </div>
          </Link>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-4">
            {isAuthenticated && user && (
              <CompactBalance coins={user.coins} gems={user.gems} />
            )}

            {!isLoading && !isAuthenticated && (
              <Link href="/login">
                <Button size="sm">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </div>
      <NavContextStrip />
    </nav>
  );
}
